/**
 * /accounting/reports/wip-aging — Work-In-Progress aging by job.
 *
 * Read-only. Surfaces stale capitalized cost (>90d open WIP balance) so
 * Steve can chase whether to release-to-COGS, write off, or invoice.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Download } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import {
  getWipAgingRows, getWipAgingSummary, WIP_AGING_BUCKETS, type WipAgingBucket,
} from "@/data/accounting/reports/wipAging";
import { exportCsv } from "@/lib/csvExport";

const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

const BUCKET_LABEL: Record<WipAgingBucket, string> = {
  "0-30": "0–30d",
  "31-60": "31–60d",
  "61-90": "61–90d",
  "91-120": "91–120d",
  "120+": "120d+",
};

const bucketTone = (b: WipAgingBucket): string => {
  if (b === "120+") return "text-destructive";
  if (b === "91-120") return "text-amber-600 dark:text-amber-500";
  return "";
};

export default function WipAgingPage() {
  useSeedGeneralLedger();
  const { journalEntries } = useGeneralLedger();
  const [asOf, setAsOf] = useState(todayIso());

  const summary = useMemo(() => getWipAgingSummary(journalEntries, asOf), [journalEntries, asOf]);
  const rows = useMemo(() => getWipAgingRows(journalEntries, asOf), [journalEntries, asOf]);

  const downloadCsv = () => {
    exportCsv(
      `wip-aging-${asOf}.csv`,
      ["Job", "Bucket", "Age (days)", "Oldest open", "Total debits", "Total credits", "Open balance", "Lines"],
      rows.map(r => [
        r.jobId, r.bucket, r.ageDays, r.oldestOpenDate ?? "",
        r.totalDebits.toFixed(2), r.totalCredits.toFixed(2),
        r.balance.toFixed(2), r.lineCount,
      ]),
    );
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to Financial Reports
          </Link>
          <h1 data-tour-id="wip-aging-table" className="text-2xl font-semibold tracking-tight">WIP Aging</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live from <code>1200 Work In Progress</code>. Jobs with stale open balances are candidates for release to COGS, invoicing, or write-off.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">As of</div>
            <Input type="date" className="h-8 w-40" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={rows.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi label="Total open WIP" value={fmt(summary.totalOpenBalance)} sub={`${summary.jobCount} jobs`} />
          {WIP_AGING_BUCKETS.map(b => (
            <Kpi
              key={b}
              label={BUCKET_LABEL[b]}
              value={fmt(summary.bucketTotals[b])}
              sub={`${summary.bucketJobCount[b]} job${summary.bucketJobCount[b] === 1 ? "" : "s"}`}
              tone={bucketTone(b)}
            />
          ))}
        </div>
        {summary.staleBalance > 0 && (
          <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-500" />
            <div>
              <div className="font-medium">{fmt(summary.staleBalance)} aged past 90 days</div>
              <div className="text-xs text-muted-foreground">
                Oldest open WIP is {summary.oldestAgeDays} days. Review jobs below — capitalized cost this old usually means the job completed without a final COGS release.
              </div>
            </div>
          </div>
        )}
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">Bucket</th>
                <th className="text-right px-3 py-2">Age</th>
                <th className="text-left px-3 py-2">Oldest open</th>
                <th className="text-right px-3 py-2">Debits</th>
                <th className="text-right px-3 py-2">Credits</th>
                <th className="text-right px-3 py-2">Open</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">No open WIP balances as of {asOf}.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.jobId} className="border-t border-border hover:bg-accent/30">
                  <td className="px-3 py-2 font-medium">
                    <Link to={`/jobs/${r.jobId}`} className="hover:underline">WO#{r.jobId}</Link>
                  </td>
                  <td className={`px-3 py-2 text-xs ${bucketTone(r.bucket)}`}>{BUCKET_LABEL[r.bucket]}</td>
                  <td className={`px-3 py-2 text-right tabular-nums text-xs ${bucketTone(r.bucket)}`}>{r.ageDays}d</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.oldestOpenDate ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(r.totalDebits)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(r.totalCredits)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold mt-1 tabular-nums ${tone ?? ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
